import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const useBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/blogs`);
        if (mounted) setBlogs(response.data);
      } catch (error) {
        console.error('Error fetching blogs:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchBlogs();
    return () => { mounted = false; };
  }, []);

  return { blogs, loading };
};

const BlogCard = ({ blog }) => (
  <Link 
    to={`/blog/${blog.slug}`} 
    className="group flex flex-col gap-4 md:gap-6 border border-border bg-card p-4 md:p-6 rounded-2xl hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
  >
    {blog.featured_image && (
      <div className="w-full aspect-[5/3] md:aspect-[4/3] rounded-xl overflow-hidden shrink-0">
        <img 
          src={blog.featured_image} 
          alt={blog.title} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
          loading="lazy"
          width="400"
          height="300"
        />
      </div>
    )}
    
    <div className="flex flex-col flex-1 justify-center">
      <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs font-medium text-muted-foreground mb-3">
        {blog.category && (
          <span className="bg-accent/10 text-accent px-2.5 py-1 rounded-full uppercase tracking-wider font-bold">
            {blog.category}
          </span>
        )}
        <div className="flex items-center gap-1">
          <Calendar size={14} />
          <span className="hidden sm:inline">{new Date(blog.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          <span className="sm:hidden">{new Date(blog.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock size={14} />
          {blog.read_time}
        </div>
      </div>
      
      <h2 className="text-xl md:text-2xl lg:text-3xl font-bold font-heading mb-2 md:mb-3 group-hover:text-accent transition-colors leading-tight">
        {blog.title}
      </h2>
      
      <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6 line-clamp-2">
        {blog.excerpt}
      </p>

      <div className="flex items-center text-sm font-bold text-accent group-hover:gap-2 transition-all mt-auto">
        Read Article <ChevronRight size={16} className="ml-1" />
      </div>
    </div>
  </Link>
);

const BlogList = () => {
  const { blogs, loading } = useBlogs();

  return (
    <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12 lg:py-20 animate-in fade-in duration-500">
      <div className="mb-8 md:mb-12">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-3 md:mb-4 tracking-tight">Blog</h1>
        <p className="text-base md:text-lg text-muted-foreground">
          Latest strategies, tips, and news on YouTube SEO.
        </p>
        <div className="w-20 h-1.5 bg-accent rounded-full mt-4 md:mt-6"></div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full"></div>
        </div>
      ) : (
        <div className="grid gap-8">
          {blogs.map((blog) => (
            <BlogCard key={blog.id} blog={blog} />
          ))}
          {blogs.length === 0 && (
            <div className="text-center py-20 text-muted-foreground border border-border rounded-xl">
              No blog posts found. Check back later!
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogList;